/**
 * MedicAiL API contract shared by apps/api and apps/web.
 * All HTTP routes are served under /api. All bodies are JSON unless noted.
 * Auth: `Authorization: Bearer <Cognito ID token>` on every route except /api/health and /api/config.
 */
import { z } from 'zod';
/* ------------------------------------------------------------------ */
/* Branding / public config                                           */
/* ------------------------------------------------------------------ */
export const BRAND = {
    productName: 'MedicAiL',
    tagline: 'Specialist AI health guidance, built by clinicians',
    description: "AI assistant here to listen to your health questions, point you to worldwide clinical-medicine guidelines, and recommend top local doctors. I'm made by Oxford Medics but am not a substitute for a doctor.",
    colors: {
        navy: '#15213F',
        royal: '#3257B8',
        mist: '#E9ECF2',
        leaf: '#3FA34D',
        gold: '#F2B632',
        white: '#FFFFFF',
    },
    fonts: { heading: 'Roboto Slab', body: 'Roboto' },
};
/* ------------------------------------------------------------------ */
/* Attachments                                                        */
/* ------------------------------------------------------------------ */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const ACCEPTED_DOC_TYPES = ['application/pdf', 'text/plain', 'text/markdown', 'text/csv'];
export const ACCEPTED_MIME_TYPES = [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_DOC_TYPES];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB per file
export const MAX_FILES_PER_MESSAGE = 5;
export const CreateUploadRequest = z.object({
    fileName: z.string().min(1).max(255),
    mimeType: z.enum(ACCEPTED_MIME_TYPES),
    sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});
/** POST /api/chats (body optional: { title? }) -> ChatSummary */
export const CreateChatRequest = z.object({ title: z.string().max(120).optional() });
/** PATCH /api/chats/:chatId { title } -> ChatSummary */
export const RenameChatRequest = z.object({ title: z.string().min(1).max(120) });
/** DELETE /api/chats/:chatId -> 204. DELETE /api/chats -> 204 (deletes all of caller's chats). */
/**
 * POST /api/chats/:chatId/messages
 * Response: `text/event-stream` (SSE). Each event is `event: <type>\ndata: <json>\n\n`
 * where the JSON is one of StreamEvent below (the `type` field duplicates the SSE event name).
 * Client may abort via AbortController; server stops the agent turn.
 */
export const SendMessageRequest = z.object({
    content: z.string().max(20_000),
    attachmentIds: z.array(z.string()).max(MAX_FILES_PER_MESSAGE).default([]),
});
/** PATCH /api/admin/users/:userId { role?, disabled? } -> AdminUserRow */
export const AdminUpdateUserRequest = z.object({
    role: z.enum(['user', 'admin']).optional(),
    disabled: z.boolean().optional(),
});
//# sourceMappingURL=index.js.map